// ============================================================================
// FNE-BRIDGE Frontend - Hook de vérification des permissions
// Centralise la logique d'habilitation pour toute l'application
// ============================================================================

import { useCallback } from 'react';
import { useAuthStore } from '../stores';

/**
 * Hook pour vérifier les permissions de l'utilisateur connecté.
 * - Admin bypass : un admin a TOUTES les permissions.
 * - Sinon, on vérifie dans `user.permissions_custom`.
 *
 * Usage :
 *   const { can, canAny, canAll } = usePermissions();
 *   if (can('factures', 'creer')) { ... }
 *   if (canAny('factures', ['creer', 'modifier'])) { ... }
 */
export function usePermissions() {
  const user = useAuthStore((s) => s.user);

  /** Vérifie une permission unique : section.action */
  const can = useCallback(
    (section: string, action: string): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      const perms = user.permissions_custom;
      if (!perms) return false;
      return (perms[section] || []).includes(action);
    },
    [user],
  );

  /** Vérifie si l'utilisateur a AU MOINS UNE des actions listées dans la section */
  const canAny = useCallback(
    (section: string, actions: string[]): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      const perms = user.permissions_custom;
      if (!perms) return false;
      const sectionPerms = perms[section] || [];
      return actions.some((a) => sectionPerms.includes(a));
    },
    [user],
  );

  /** Vérifie si l'utilisateur a TOUTES les actions listées */
  const canAll = useCallback(
    (section: string, actions: string[]): boolean => {
      if (!user) return false;
      if (user.role === 'admin') return true;
      const perms = user.permissions_custom;
      if (!perms) return false;
      const sectionPerms = perms[section] || [];
      return actions.every((a) => sectionPerms.includes(a));
    },
    [user],
  );

  /** Vérifie si l'utilisateur peut voir une section (permission 'voir') */
  const canView = useCallback(
    (section: string): boolean => {
      // Cas spécial : entreprises est visible si on a "clients" OU "fournisseurs"
      if (section === 'entreprises') {
        return can(section, 'clients') || can(section, 'fournisseurs');
      }
      return can(section, 'voir');
    },
    [can],
  );

  return { can, canAny, canAll, canView, isAdmin: user?.role === 'admin' };
}
